import { InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useLayoutEffect, useState } from "react";
import { toast } from "react-hot-toast";
import DialogWithCloseButton from "@/components/common/modals/dialogWithCloseButton";
import { TEXT_SECONDARY } from "@/config/mui/colorPalette";
import { useVerifyCashCountMutation } from "@/graphql/mutations/sales/createCashCountVerification.graphql.types";
import { useMedspaId } from "@/hooks/common/useMedspaId";
import useErrorLogger from "@/utils/useErrorLogger";

type CashDrawerDialogProps = {
  isOpen: boolean;
  onSuccess?: () => void;
  onSkip?: () => void;
};

export default function CashDrawerDialog({
  isOpen,
  onSuccess,
  onSkip,
}: CashDrawerDialogProps) {
  const medspaId = useMedspaId();
  const logError = useErrorLogger();

  const [cashAmount, setCashAmount] = useState<string>("");
  const [showError, setShowError] = useState(false);

  useLayoutEffect(() => {
    if (isOpen) {
      setCashAmount("");
      setShowError(false);
    }
  }, [isOpen]);

  const [verifyCashCount, { loading }] = useVerifyCashCountMutation();

  const handleRecordCashDrawer = async (amount: string) => {
    if (!medspaId) {
      toast.error("Unable to record the cash amount. Please try again.");

      return;
    }
    try {
      await verifyCashCount({
        variables: {
          medspaId,
          amount,
        },
      });
      toast.success("Cash amount recorded successfully");

      return true;
    } catch (error) {
      logError(error);
      toast.error("Unable to record the cash amount. Please try again.");

      return false;
    }
  };

  const processInput = (value: string) => {
    // Replace all commas with dots (normalize decimal separators)
    value = value.replaceAll(",", ".");

    // Remove leading zeros (e.g., "007" becomes "7", but "0.7" stays "0.7")
    value = value.replace(/^0+(?=\d)/, "");

    // Keep only digits, commas, and dots - remove all other characters
    value = value.replace(/[^,.0-9]/g, "");

    // If input starts with ".", prepend a 0 (e.g., ".25" → "0.25")
    if (value.startsWith(".")) {
      value = "0" + value;
    }

    const parts = value.split(".");

    // Remove leading zeros only from integer part
    const integerPart = parts[0].replace(/^0+(?!$)/, "");

    // If there's a decimal part, ensure it has at most 2 digits
    if (parts.length > 1) {
      parts[1] = parts[1].slice(0, 2);
    }

    return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
  };

  const handleSubmit = async () => {
    const sanitizedCashAmount = cashAmount.replace(/\.$/, "");
    const numericValue = parseFloat(sanitizedCashAmount);

    if (isNaN(numericValue) || numericValue < 0) {
      setShowError(true);

      return;
    }

    setShowError(false);
    const success = await handleRecordCashDrawer(sanitizedCashAmount);
    if (success) onSuccess?.();
  };

  const handleCancel = () => {
    setCashAmount("");
    setShowError(false);
    onSkip?.();
  };

  return (
    <DialogWithCloseButton
      isOpen={isOpen}
      title="Record cash drawer amount"
      content={
        <Stack sx={{ gap: 2, alignItems: "center" }}>
          <Typography sx={{ color: TEXT_SECONDARY }}>
            Please enter the current amount of cash in the drawer in the field
            below.
          </Typography>
          <TextField
            placeholder="Cash in drawer"
            value={cashAmount}
            onChange={(e) => {
              setCashAmount(processInput(e.target.value));
              setShowError(false);
            }}
            size="small"
            error={showError}
            helperText={showError && "Please enter a valid cash amount"}
            fullWidth
            type="text"
            inputMode="decimal"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: TEXT_SECONDARY }}>$</Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  display: "none",
                },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
            }}
          />
        </Stack>
      }
      primaryButtonText="Record cash"
      cancelButtonText="Skip"
      handlePrimaryClick={handleSubmit}
      handleCancel={handleCancel}
      loading={loading}
    />
  );
}
